import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'What is URL-safe Base64?', a: 'URL-safe Base64 is a variant of Base64 that replaces the "+" and "/" characters with "-" and "_", and usually omits the "=" padding. This lets the encoded string be used safely in URLs, query strings, filenames and cookies without escaping.' },
  { q: 'When should I use URL-safe Base64?', a: 'Use it whenever the encoded value travels inside a URL or HTTP header — for example JSON Web Tokens (JWTs), OAuth tokens, password-reset links and API identifiers all use URL-safe Base64.' },
  { q: 'What is the difference between standard and URL-safe Base64?', a: 'They encode the same data; only two characters differ. Standard Base64 uses "+", "/" and "=" padding, while URL-safe Base64 uses "-", "_" and drops the padding so the string survives URL encoding unchanged.' },
  { q: 'Can I decode a URL-safe Base64 string here?', a: 'Yes. Switch to Decode mode above and paste your URL-safe string — the tool restores the standard alphabet and padding automatically before decoding.' },
];

export default function UrlSafePage() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="URL-Safe Base64 Encode & Decode Online | EasyBase64"
        description="Free online URL-safe Base64 encoder and decoder. Convert text to URL-safe Base64 (using - and _ without padding) and back — ideal for JWTs, tokens and URLs."
        path="/url-safe-base64"
        jsonLd={[
          softwareApplicationSchema({
            name: 'URL-Safe Base64 Converter',
            description: 'Free online tool to encode and decode URL-safe Base64 instantly in the browser.',
            path: '/url-safe-base64',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'URL-Safe Base64', path: '/url-safe-base64' },
          ]),
        ]}
      />

      <PageHero
        title="URL-Safe Base64 Encoder & Decoder"
        subtitle={<>Encode and decode the URL-safe Base64 variant — using <code>-</code> and <code>_</code> with no padding — perfect for JWTs, tokens and query strings. Need the standard variant? Use the <Link to="/" className="underline">main converter</Link>.</>}
      />

      <div className="mb-12">
        <Base64Tool initialMode="encode" variant="url-safe" />
      </div>

      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12`}>
        <h2 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-4`}>Standard vs. URL-safe Base64</h2>
        <div className={`space-y-3 ${themeClasses.textSecondary} leading-relaxed`}>
          <p>Both alphabets encode the same bytes — only three things change:</p>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>+</strong> becomes <strong>-</strong> (minus)</li>
            <li><strong>/</strong> becomes <strong>_</strong> (underscore)</li>
            <li>Trailing <strong>=</strong> padding is removed</li>
          </ul>
          <p>These swaps keep the string intact when placed in a URL, cookie or filename, where <code>+</code>, <code>/</code> and <code>=</code> would otherwise need percent-encoding.</p>
        </div>
      </section>

      <FaqSection title="URL-safe Base64 FAQ" faqs={FAQS} />
    </>
  );
}
